<template>
  <div class="w-full">
    <div class="flex items-center justify-between px-4 py-3 border-b border-default">
      <span class="text-sm font-semibold text-heading">{{ t('notificationsPopover.title') }}</span>
      <Button variant="link" size="sm" data-testid="notification-mark-all" @click="markAllRead">{{ t('notificationsPopover.markAllRead') }}</Button>
    </div>
    <InlineNotice v-if="popoverError" variant="error" class="rounded-none">{{ popoverError }}</InlineNotice>
    <!-- The list is one read, so it uses the suite's shared read state rather than a
         sentence written here (ADR 0068 rule 5): the failure says WHY and offers a
         retry. Keeping rows on a failed read is ReadState's default — a reload that
         fails behind a successful mark-all keeps the rows on screen and warns above
         them, instead of taking them away. The list's own copy stays: "you're all
         caught up" is a better empty state than the generic one. -->
    <ReadState
      class="max-h-[360px] overflow-y-auto divide-y divide-[var(--border-default)]"
      :loading="loading"
      :error="loadError"
      :empty="!items.length"
      :retry="load"
    >
      <template #loading>
        <p class="px-4 py-6 text-sm text-tertiary">{{ t('notificationsPopover.loading') }}</p>
      </template>
      <template #empty>
        <EmptyState :title="t('notificationsPopover.empty')" />
      </template>
      <!-- Unread only (issue 1036): a row marked read stays for a moment with its dot
           emptied, then leaves; read history lives in the inbox (footer link). -->
      <TransitionGroup name="lumi-notif-row">
      <div v-for="n in shown" :key="n.id" class="flex items-center gap-3 px-4 py-3" data-testid="notification-row">
        <!-- The read dot is a tiny glyph, not a labelled control: the one place a raw
             button stays (scripts/allowlists/ds-controls.json). -->
        <button
          type="button"
          class="shrink-0 w-3 h-3 rounded-full border-2 border-[var(--btn-primary-bg)]"
          :class="n.readAt ? 'bg-transparent' : 'bg-[var(--btn-primary-bg)]'"
          :aria-label="n.readAt ? t('notificationsPopover.markUnread') : t('notificationsPopover.markRead')"
          data-testid="notification-read-toggle"
          :data-read="n.readAt ? 'true' : 'false'"
          @click="toggleRead(n)"
        ></button>
        <!-- The text opens the INBOX, not the linked object (#427). A row here is a
             headline; the page that can show a person the whole of it — filtered,
             paged, dated — is the inbox. `title` puts the untruncated sentence one
             hover away. The row itself is the interaction surface. -->
        <button type="button" class="min-w-0 flex-1 text-left" :title="n.text" @click="activate(n)">
          <span
            class="block truncate text-sm text-heading"
            :class="n.readAt ? 'font-normal' : 'font-semibold'"
          >
            {{ n.text }}
          </span>
        </button>
        <!-- The linked object, and the only control that goes there (#427). Present
             only on rows that actually carry a callToAction, so the arrow is a
             promise the row can keep. -->
        <IconButton
          v-if="n.ctaConstant"
          size="sm"
          :label="t('notificationsPopover.openTarget')"
          data-testid="notification-follow"
          @click="follow(n)"
        >
          <LumiIcon name="next" class="w-4 h-4" />
        </IconButton>
      </div>
      </TransitionGroup>
    </ReadState>
    <div class="px-4 py-2.5 border-t border-default text-center">
      <a :href="inboxUrl" class="text-xs text-heading hover:underline" data-testid="notification-inbox-link" @click="onInboxLink">{{ t('notificationsPopover.goToAll') }}</a>
    </div>
    <!-- Whatever else an app wants in its popover (time-ui's web-push opt-in). The
         popover itself knows nothing about it. -->
    <slot name="extra" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { AxiosInstance } from 'axios';
import { useI18n } from 'vue-i18n';
import { Button, EmptyState, IconButton, InlineNotice, LumiIcon } from '@lumi/ui';
import { useApiMutation } from '../composables/useApiMutation';
import ReadState from './ReadState.vue';
import { buildCallToActionHref } from '../utils/moduleSwitcher';

/** A notification row as notification-api serves it. */
export interface NotificationRow {
  id: string;
  sender: string;
  text: string;
  ctaConstant: string | null;
  ctaContext?: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

// ONE popover for the suite's six apps (ADR 0026). The notification-api client is
// INJECTED (`api`) — app-kit imports no app's api.ts — and so is everything that
// differs between a module UI and the workspace: where a resolved link goes, and
// how the inbox is opened.
const props = withDefaults(
  defineProps<{
    /** The app's notification-api client (`notificationApi`). */
    api: AxiosInstance;
    /**
     * The session handoff a cross-app link rides on, handed down by the app chrome
     * that already builds it for the module switcher — the popover has no business
     * assembling a second copy of the token rule (ADR 0024).
     */
    handoff?: Record<string, string | null | undefined>;
    /** The tenant's workspace origin (`runtimeWorkspaceUrl()`): where workspace-owned
     * links, and the inbox, live for a module UI. */
    workspaceUrl?: string;
    /** The inbox address. Defaults to `<workspaceUrl>/notifications`; the workspace
     * passes its own route. */
    inboxHref?: string;
    /**
     * How a followed row's response becomes an address. Defaults to app-kit's
     * `buildCallToActionHref` (module routing + handoff); the workspace passes its own
     * resolver because it also follows its own administration pages in place and the
     * public landing site. A null result stays put, popover open.
     */
    resolveTarget?: (row: NotificationRow, payload: unknown) => string | null;
    /** Opens the inbox. Default: a full page load. The workspace routes in-app. */
    openInbox?: (href: string) => void;
    /** The workspace is a demo: the feed leaves out what only the real workspace owns (issue 803). */
    demo?: boolean;
  }>(),
  { handoff: () => ({}), workspaceUrl: '', demo: false },
);

const emit = defineEmits<{ close: []; changed: [] }>();
const { t } = useI18n();
const items = ref<NotificationRow[]>([]);
// The popover lists UNREAD notifications only (issue 1036): it is the "what is new"
// glance; the inbox behind the footer link keeps the read ones. The read asks for up
// to 20 so a row that leaves is replaced by the next unread one, and five show.
const shown = computed(() => items.value.slice(0, 5));
/**
 * How long a row marked read stays, its dot emptied, before it leaves (issue 1036) —
 * long enough to see that the click landed. The leave itself is a fade the global
 * reduced-motion block shortens to nothing; the pause is not motion and stays.
 */
const READ_ROW_HOLD_MS = 900;
const removalTimers = new Set<ReturnType<typeof setTimeout>>();
const loading = ref(false);
// The thrown value: the shared read state turns it into the localized reason.
const loadError = ref<unknown>(null);
const inboxUrl = computed(
  () => props.inboxHref ?? `${props.workspaceUrl.replace(/\/$/, '')}/notifications`,
);

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    // `suppressGlobalError`: the failure of this read is answered HERE, by ReadState,
    // in the popover the user opened — not on the app's global channel.
    const { data } = await props.api.get('/notifications', {
      params: { pageSize: 20, read: 'unread', ...(props.demo ? { demo: 1 } : {}) },
      suppressGlobalError: true,
    });
    items.value = data.items ?? [];
  } catch (err) {
    // The thrown value, not a sentence: the shared read state renders the localized
    // `errors.*` reason for it (ADR 0068 rule 5).
    loadError.value = err;
  } finally {
    loading.value = false;
  }
}
// One signal the shell refreshes its unread badge from: the window event is the
// channel the inbox page shares with the chrome, `changed` the popover's own.
function changed() {
  window.dispatchEvent(new Event('lumi:notifications-changed'));
  emit('changed');
}

// --- The three writes (issue 195, D4) ---------------------------------------
//
// All three are tier 2 (`inlineEdit`): the dot, the row weight and the unread badge
// ARE the confirmation, so none of them toasts.
//
// Marking one row read/unread keeps the app's default failure channel. Mark-all and
// follow report failure `inPlace`, into ONE line at the top of the popover
// (`popoverError`): a toast for them would outlive the popover that caused it, and
// a refused follow must say so where the user is looking. The channel is NOT
// `handled` — nothing on this surface consumes the outcome on the user's behalf.
const { mutate: markReadRequest } = useApiMutation({
  tier: 'inlineEdit',
  run: async (n: NotificationRow, read: boolean) => {
    await props.api.patch(`/notifications/${n.id}/read`, { read });
  },
});

const {
  formError: markAllError,
  mutate: markAllRequest,
  reset: resetMarkAll,
} = useApiMutation({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async () => {
    await props.api.post('/notifications/mark-all', { read: true });
  },
});

const {
  formError: followError,
  mutate: followRequest,
  reset: resetFollow,
} = useApiMutation({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async (n: NotificationRow) => {
    const { data } = await props.api.post(`/notifications/${n.id}/follow`);
    return data as unknown;
  },
});

// Only one of the two can be the last failure the user saw, so the refs collapse into
// one rendered line and each handler clears the other first.
const popoverError = computed(() => markAllError.value || followError.value);

function clearPopoverError() {
  resetMarkAll();
  resetFollow();
}

async function markOne(n: NotificationRow, read: boolean) {
  if (!!n.readAt === read) return;
  // Optimistic: the dot is the confirmation, so it moves on click, not a round trip
  // later. `previous` is restored when the write is refused, so the popover never
  // shows a read state the server rejected.
  const previous = n.readAt;
  const flippedAt = Date.now();
  n.readAt = read ? new Date().toISOString() : null;
  const outcome = await markReadRequest(n, read);
  if (!outcome.ok) n.readAt = previous;
  // The shell re-reads its unread count from the server, so it is told AFTER the write
  // has landed (or been refused): signalled on the optimistic flip it would read the
  // old count and the badge would stay one behind.
  changed();
  // A landed "read" takes the row out of this unread list once the person has seen
  // the dot empty. Counted from the click, so a slow write does not add to the pause;
  // a row marked unread again in the meantime stays.
  if (read && outcome.ok) removeOnceSeen(n, flippedAt);
}
// Takes a row that went read out of this unread list, READ_ROW_HOLD_MS after
// `flippedAt`, unless it was marked unread again in the meantime.
function removeOnceSeen(n: NotificationRow, flippedAt: number) {
  const timer = setTimeout(() => {
    removalTimers.delete(timer);
    if (n.readAt) items.value = items.value.filter((row) => row.id !== n.id);
  }, Math.max(0, READ_ROW_HOLD_MS - (Date.now() - flippedAt)));
  removalTimers.add(timer);
}
async function toggleRead(n: NotificationRow) {
  await markOne(n, !n.readAt);
}
async function markAllRead() {
  clearPopoverError();
  const outcome = await markAllRequest();
  if (!outcome.ok) return;
  changed();
  await load();
}

function leaveTo(href: string) {
  window.location.href = href;
}
async function follow(n: NotificationRow) {
  clearPopoverError();
  const outcome = await followRequest(n);
  // The navigation below depends on the response, so a refused follow must not
  // navigate — it leaves the popover open with `popoverError` rendered.
  if (!outcome.ok) return;
  n.readAt = new Date().toISOString();
  // A followed row is read: it leaves this unread list like a dot-marked one —
  // what is seen when the target resolves to nowhere and the panel stays open.
  removeOnceSeen(n, Date.now());
  changed();
  // The owning module resolved the callToAction to { module, deepLink }; app-kit
  // turns that into an address THIS browser can reach (#426).
  const resolved = props.resolveTarget
    ? props.resolveTarget(n, outcome.data)
    : buildCallToActionHref(
        (outcome.data as { callToAction?: { module?: string; deepLink?: string } } | null)?.callToAction,
        props.handoff,
        props.workspaceUrl,
      ) || null;
  if (!resolved) {
    // An app that resolves the target itself (the workspace) decided this row has
    // nowhere to go: it stays put, panel open. Without one, a followed row with no
    // target falls back to the inbox, as every module app always did.
    if (props.resolveTarget) return;
    emit('close');
    (props.openInbox ?? leaveTo)(inboxUrl.value);
    return;
  }
  const target = resolved;
  // The panel is shut before the navigation so the destination never renders under
  // an open popover (issue 391) — whether or not it unloads the page.
  emit('close');
  leaveTo(target);
}
// Clicking the text opens the inbox (#427), marking the row read on the way — a row
// you clicked into is a row you have seen. It does not follow the link: that is the
// arrow's job. The dot stays the only control that flips read state without going
// anywhere.
async function activate(n: NotificationRow) {
  await markOne(n, true);
  emit('close');
  (props.openInbox ?? leaveTo)(inboxUrl.value);
}
// The footer link: the shell's panel is shut first, and an in-app inbox is routed
// rather than loaded (issue 391).
function onInboxLink(event: MouseEvent) {
  emit('close');
  if (props.openInbox) {
    event.preventDefault();
    props.openInbox(inboxUrl.value);
  }
}

onMounted(load);
onBeforeUnmount(() => {
  for (const timer of removalTimers) clearTimeout(timer);
  removalTimers.clear();
});
</script>

<style scoped>
/* A read row leaving the unread list (issue 1036): a short fade. Under
   prefers-reduced-motion the global motion block (tokens/motion.css) cuts it to
   an instant removal. */
.lumi-notif-row-leave-active{transition:opacity var(--dur-base) var(--ease-standard);}
.lumi-notif-row-leave-to{opacity:0;}
</style>
